import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MobileLayout from "../components/layout/MobileLayout";
import { api } from "../lib/api";
import {
  ArrowUpRight, BarChart3, Play, Clock,
  BookOpen, Wallet as WalletIcon, ArrowDownToLine, Youtube, Trophy, Zap, Sparkles, Crown, TrendingUp, Calendar, ChevronRight
} from "lucide-react";
import { Sheet, SheetContent } from "../components/ui/sheet";
import FullscreenVideo from "../components/FullscreenVideo";
import LivePlayers from "../components/LivePlayers";

function format12(t) {
  if (!t) return "--";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${ap}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [howOpen, setHowOpen] = useState(false);
  const [historyMarket, setHistoryMarket] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const loadMarkets = () => api.get("/markets").then(r => setMarkets(r.data)).catch(()=>{});
    const loadUnread  = () => api.get("/chat/unread").then(r => setChatUnread(r.data.count || 0)).catch(()=>{});
    Promise.all([api.get("/markets"), api.get("/settings")])
      .then(([m, s]) => { setMarkets(m.data); setSettings(s.data); })
      .finally(() => setLoading(false));
    loadUnread();
    const pollMarkets = setInterval(loadMarkets, 30000);
    const uid = setInterval(loadUnread, 8000);
    // Refresh immediately when app returns to foreground (tab switch, APK resume)
    const onVisible = () => {
      if (document.visibilityState === "visible") { loadMarkets(); loadUnread(); }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearInterval(pollMarkets); clearInterval(uid);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  const tg = settings.telegram_url || "https://t.me/reddyclub";
  const liveCount = markets.filter(m => m.is_market_active).length;

  return (
    <MobileLayout>
      {/* ===== 4 Premium Action Tiles — matching mockup ===== */}
      <div className="grid grid-cols-4 gap-2.5 mb-4" data-testid="quick-actions">
        <BrandTile to="/deposit" label="Deposit" subtitle="FAST & SECURE" testid="action-deposit"
          bg="from-emerald-400 via-emerald-500 to-emerald-700" glow="shadow-emerald-500/50">
          <DepositIcon />
        </BrandTile>
        <BrandTile to="/withdraw" label="Withdrawal" subtitle="INSTANT" testid="action-withdraw"
          bg="from-rose-500 via-red-600 to-rose-800" glow="shadow-rose-500/50">
          <WithdrawalIcon />
        </BrandTile>
        <BrandTile href={tg} label="Telegram" subtitle="JOIN NOW" testid="action-telegram"
          bg="from-sky-400 via-blue-500 to-blue-700" glow="shadow-sky-500/50">
          <TelegramIcon />
        </BrandTile>
        <BrandTile to="/chat" label="Live Chat" subtitle="24×7 SUPPORT" testid="action-whatsapp"
          bg="from-purple-500 via-fuchsia-600 to-purple-800" glow="shadow-purple-500/50" badge={chatUnread}>
          <HeadphonesIcon />
        </BrandTile>
      </div>

      {/* ===== Daily joined users badge & poster carousel moved to admin panel only ===== */}

      {/* How to Play — Gold banner with flanking crowns */}
      <button onClick={() => setHowOpen(true)} data-testid="how-to-play-btn" className="block w-full mb-4 active:scale-[0.98] transition">
        <div className="relative rounded-2xl bg-gold-gradient shadow-2xl overflow-hidden btn-shine">
          <div className="absolute inset-0 pattern-grid opacity-30" />
          <div className="relative flex items-center gap-2 px-3 py-3">
            <Crown className="w-9 h-9 text-blue-950 shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" fill="currentColor" strokeWidth={1.5} />
            <div className="flex-1 flex items-center justify-center gap-3">
              <span className="w-10 h-10 rounded-full bg-blue-950 text-yellow-400 flex items-center justify-center shadow-inner shrink-0">
                <Play className="w-5 h-5 fill-yellow-400 ml-0.5" strokeWidth={0} />
              </span>
              <div className="text-left">
                <div className="font-display font-black text-blue-950 text-lg leading-none tracking-tight">HOW TO PLAY</div>
                <div className="text-[9px] font-black text-blue-950/80 tracking-[0.2em] mt-1">STEP BY STEP GUIDE</div>
              </div>
            </div>
            <Crown className="w-9 h-9 text-blue-950 shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]" fill="currentColor" strokeWidth={1.5} />
          </div>
        </div>
      </button>

      {/* Instant Games — Dragon Tiger + Coin Flip (30s rounds, 2× payout) */}
      <div className="mb-4" data-testid="instant-games-section">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="w-1.5 h-4 bg-yellow-400 rounded-full shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
          <span className="font-display font-black text-gold-static text-sm tracking-widest">INSTANT GAMES · 30s</span>
          <span className="ml-auto text-[9px] font-black text-emerald-300 tracking-widest bg-emerald-500/15 border border-emerald-400/40 px-2 py-0.5 rounded-full">LIVE</span>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Link to="/games/dragon-tiger" data-testid="tile-dragon-tiger" className="relative rounded-2xl overflow-hidden border-2 border-yellow-400/60 active:scale-95 transition block shadow-lg">
            <img src="/games/dragon-tiger-tile.jpg" alt="Dragon Tiger" className="w-full h-auto block" loading="lazy" />
            <span className="absolute top-2 left-2 z-[6]"><LivePlayers seed="dragon-tiger" /></span>
          </Link>
          <Link to="/games/coin-flip" data-testid="tile-coin-flip" className="relative rounded-2xl overflow-hidden border-2 border-yellow-300/80 active:scale-95 transition block shadow-lg">
            <img src="/games/coin-flip-tile.jpg" alt="Coin Flip" className="w-full h-auto block" loading="lazy" />
            <span className="absolute top-2 left-2 z-[6]"><LivePlayers seed="coin-flip" /></span>
          </Link>
        </div>
        {/* Aviator — full-width bottom row */}
        <Link to="/games/aviator" data-testid="tile-aviator" className="relative block rounded-2xl overflow-hidden border-2 border-red-400/70 active:scale-95 transition mt-2 shadow-lg">
          <img src="/games/aviator-tile.jpg" alt="Aviator" className="w-full h-auto block" loading="lazy" />
          <span className="absolute top-2 left-2 z-[6]"><LivePlayers seed="aviator" /></span>
        </Link>
      </div>

      {/* LIVE MARKETS heading — crown flanked */}
      <div className="flex items-center gap-3 mb-3 mt-1">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent" />
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-yellow-400" fill="currentColor" />
          <span className={`font-black tracking-[0.2em] text-sm ${liveCount > 0 ? "text-gold-static" : "text-cyan-200/70"}`}>
            {liveCount > 0 ? `${liveCount} LIVE MARKETS` : "ALL MARKETS CLOSED"}
          </span>
          <Crown className="w-4 h-4 text-yellow-400" fill="currentColor" />
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent" />
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-slate-800/40 border border-cyan-400/15 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="space-y-3" data-testid="market-list">
          {markets.map((m) => <MarketCard key={m.id} m={m} onChart={() => setHistoryMarket(m)} />)}
        </div>
      )}

      <div className="text-center text-xs text-cyan-200/60 mt-6 mb-2 flex items-center justify-center gap-1">
        <Crown className="w-3 h-3 text-yellow-400" />
        <span>Powered by Reddy Club · Play Responsibly</span>
        <Crown className="w-3 h-3 text-yellow-400" />
      </div>

      {/* How to Play Sheet — 3 cards, click any = full-screen play */}
      <Sheet open={howOpen} onOpenChange={setHowOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto bg-gradient-to-br from-blue-950 via-blue-900 to-indigo-950 border-t-4 border-yellow-400 pb-32">
          <div className="text-center mb-4 pt-2">
            <div className="inline-flex items-center gap-2 bg-gold-gradient text-blue-900 px-4 py-1.5 rounded-full text-xs font-bold mb-2">
              <Youtube className="w-4 h-4" /> VIDEO TUTORIALS
            </div>
            <div className="font-display font-black text-2xl text-gold-shine">Reddy Club Guide</div>
            <div className="text-xs text-blue-200 mt-1">Kisi bhi card pe tap karo — full-screen video play hogi</div>
          </div>
          <div className="space-y-3">
            <VideoTile Icon={BookOpen}        title="How to Play"     desc="Game rules, betting basics"      url={settings.youtube_how_to_play}     accent="from-rose-500 to-pink-600"     testid="how-play"     onOpen={() => setActiveVideo({ url: settings.youtube_how_to_play, title: "How to Play" })} />
            <VideoTile Icon={WalletIcon}      title="How to Deposit"  desc="Wallet me paise kaise daalein"   url={settings.youtube_how_to_deposit}  accent="from-emerald-500 to-teal-600"  testid="how-deposit"  onOpen={() => setActiveVideo({ url: settings.youtube_how_to_deposit, title: "How to Deposit" })} />
            <VideoTile Icon={ArrowDownToLine} title="How to Withdraw" desc="Jeeti hui rakam kaise nikaalein" url={settings.youtube_how_to_withdraw} accent="from-sky-500 to-indigo-600"    testid="how-withdraw" onOpen={() => setActiveVideo({ url: settings.youtube_how_to_withdraw, title: "How to Withdraw" })} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Fullscreen video overlay */}
      <FullscreenVideo open={!!activeVideo} onClose={() => setActiveVideo(null)} url={activeVideo?.url} title={activeVideo?.title} />

      <ResultHistorySheet market={historyMarket} onClose={() => setHistoryMarket(null)} />
    </MobileLayout>
  );
}

function VideoTile({ Icon, title, desc, url, accent, testid, onOpen }) {
  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className="w-full flex items-center gap-3 p-3 rounded-2xl border-2 border-yellow-400/50 bg-blue-950/60 backdrop-blur-sm shadow-xl active:scale-[0.98] transition text-left"
    >
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center shadow-lg shrink-0`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-black text-white text-base leading-tight">{title}</div>
        <div className="text-[11px] text-blue-200 mt-0.5">{desc}</div>
      </div>
      <div className="w-9 h-9 rounded-full bg-gold-gradient text-blue-950 flex items-center justify-center shadow-lg shrink-0">
        <Play className="w-4 h-4 fill-blue-950 ml-0.5" strokeWidth={0} />
      </div>
    </button>
  );
}

function VideoSection({ Icon, title, desc, url, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl overflow-hidden border-2 border-yellow-400/40 bg-blue-950/60 backdrop-blur-sm shadow-2xl">
      <div className="flex items-center gap-3 p-3 border-b border-yellow-400/20">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${accent} text-white flex items-center justify-center shadow-lg shrink-0`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-white text-base leading-tight">{title}</div>
          <div className="text-[11px] text-blue-200 mt-0.5">{desc}</div>
        </div>
      </div>
      <VideoPlayer url={url} testid={`${testid}-player`} />
    </div>
  );
}

/**
 * Renders any of these inline:
 * - YouTube (watch/youtu.be/shorts) → embedded iframe
 * - .mp4 / .webm / .mov → HTML5 <video>
 * - null/empty → placeholder "coming soon"
 */
export function VideoPlayer({ url, testid, poster }) {
  if (!url) {
    return (
      <div className="aspect-video bg-blue-900/40 flex flex-col items-center justify-center text-blue-200 gap-2" data-testid={`${testid}-empty`}>
        <Youtube className="w-10 h-10 opacity-40" />
        <div className="text-xs font-semibold">Video coming soon</div>
      </div>
    );
  }
  const yt = extractYouTubeId(url);
  if (yt) {
    return (
      <div className="aspect-video bg-black">
        <iframe
          data-testid={testid}
          src={`https://www.youtube.com/embed/${yt}?rel=0&modestbranding=1`}
          title="Video Tutorial"
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="aspect-video bg-black">
      <video data-testid={testid} src={url} controls playsInline poster={poster} className="w-full h-full object-contain bg-black" />
    </div>
  );
}

function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function ResultHistorySheet({ market, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const nowIst = new Date(Date.now() + 5.5 * 3600 * 1000);
  const [year, setYear] = useState(nowIst.getUTCFullYear());
  const [month, setMonth] = useState(nowIst.getUTCMonth() + 1);

  useEffect(() => {
    if (!market) { setData(null); return; }
    setLoading(true);
    api.get(`/markets/${market.id}/result-history?limit=200&year=${year}&month=${month}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [market, year, month]);

  const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = [];
  for (let y = nowIst.getUTCFullYear(); y >= nowIst.getUTCFullYear() - 4; y--) years.push(y);

  const fmtDate = (d) => {
    try {
      const dt = new Date(d + "T00:00:00");
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", weekday: "short" });
    } catch { return d; }
  };

  return (
    <Sheet open={!!market} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto border-t-2 border-cyan-400/40 pb-32"
        style={{ background: "linear-gradient(180deg, #0a1430 0%, #060a1b 100%)" }}>
        <div className="text-center mb-3">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest"
            style={{ background: "linear-gradient(135deg,#fbbf24,#f59e0b)", color: "#0b1230" }}>
            <TrendingUp className="w-3 h-3" /> Result History
          </div>
          <div className="font-display font-black text-2xl text-gold-static mt-2" data-testid="history-market-name">{market?.name}</div>
          <div className="text-[11px] text-cyan-200/70 mt-1">Newest results on top · Month select karein</div>
        </div>

        {/* Month & Year filter */}
        <div className="flex gap-2 mb-4 sticky top-0 pt-1 pb-2 z-10 backdrop-blur-sm"
          style={{ background: "linear-gradient(180deg, #0a1430 0%, rgba(10,20,48,0.6) 100%)" }} data-testid="history-filters">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} data-testid="history-month"
            className="flex-1 h-11 rounded-xl border-2 border-cyan-400/30 bg-black/40 text-cyan-100 font-bold px-3 focus:outline-none focus:border-cyan-400">
            {MONTHS.map((m, i) => <option key={i} value={i + 1} className="bg-slate-900">{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="history-year"
            className="w-28 h-11 rounded-xl border-2 border-cyan-400/30 bg-black/40 text-cyan-100 font-bold px-3 focus:outline-none focus:border-cyan-400">
            {years.map(y => <option key={y} value={y} className="bg-slate-900">{y}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 bg-slate-800/40 border border-cyan-400/15 rounded-xl animate-pulse" />)}</div>
        ) : (
          <div className="space-y-2" data-testid="history-list">
            {(data?.history || []).length === 0 ? (
              <div className="text-center text-cyan-200/60 text-sm py-8 border-2 border-dashed border-cyan-400/25 rounded-xl">
                <Calendar className="w-10 h-10 mx-auto opacity-40 mb-2 text-cyan-300" />
                Is month me abhi tak result declare nahi hua
              </div>
            ) : (
              (data?.history || []).map((row, i) => (
                <div key={i}
                  className="rounded-xl px-4 py-3 flex items-center justify-between shadow-lg transition"
                  style={{ background: "rgba(10,24,51,0.65)", border: "1px solid rgba(34,211,238,0.25)" }}
                  data-testid={`history-row-${row.date}`}>
                  <div>
                    <div className="text-xs font-bold text-cyan-300 uppercase tracking-wide">{fmtDate(row.date)}</div>
                    <div className="text-[10px] text-cyan-200/60 mt-0.5 font-mono">{row.date}</div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-400 to-yellow-500 text-blue-950 font-black text-2xl px-4 py-1.5 rounded-lg tabular-nums shadow-md">{row.result}</div>
                </div>
              ))
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function BrandTile({ to, href, label, subtitle, children, bg, glow, testid, badge = 0 }) {
  const inner = (
    <div className="relative group" data-testid={testid}>
      <div className={`relative aspect-square rounded-2xl bg-gradient-to-br ${bg} shadow-2xl ${glow} shadow-lg active:scale-95 transition-transform btn-shine overflow-hidden ring-1 ring-white/20`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
        <div className="absolute -top-4 -right-4 w-12 h-12 bg-white/25 rounded-full blur-xl" />
        <div className="relative h-full flex flex-col items-center justify-between py-2 px-1">
          <div className="w-10 h-10 flex items-center justify-center text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]">
            {children}
          </div>
          <div className="text-center">
            <div className="font-display font-black text-white text-[11px] leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">{label}</div>
            <div className="text-[7px] font-black text-white/85 tracking-[0.15em] mt-0.5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">{subtitle}</div>
          </div>
        </div>
        {badge > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-yellow-400 text-blue-950 text-[10px] font-black flex items-center justify-center shadow-lg animate-pulse border border-white" data-testid={`${testid}-badge`}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer">{inner}</a>;
  if (to) return <Link to={to}>{inner}</Link>;
  return inner;
}

function BrandAction({ to, href, label, children, bg, ring, testid, badge = 0 }) {
  const inner = (
    <div className="flex flex-col items-center gap-1" data-testid={testid}>
      <div className={`relative w-14 h-14 rounded-2xl bg-gradient-to-br ${bg} text-white flex items-center justify-center shadow-xl active:scale-95 transition-transform btn-shine ring-2 ${ring} ring-offset-1 ring-offset-white`}>
        {children}
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center shadow-lg animate-pulse border-2 border-white" data-testid={`${testid}-badge`}>
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </div>
      <span className="text-[11px] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" data-testid={`label-${testid}`}>{label}</span>
    </div>
  );
  if (href) return <a href={href} target="_blank" rel="noreferrer">{inner}</a>;
  return <Link to={to}>{inner}</Link>;
}

/* ==================== TODAY'S WINNERS TICKER ==================== */
function TodayWinnersTicker() {
  const [winners, setWinners] = useState([]);
  const [visibleIdx, setVisibleIdx] = useState(0);

  useEffect(() => {
    const fetchWinners = () => api.get("/showcase-winners?limit=30").then(r => setWinners(r.data || [])).catch(() => {});
    fetchWinners();
    const refresh = setInterval(fetchWinners, 30000);
    return () => clearInterval(refresh);
  }, []);

  useEffect(() => {
    if (winners.length <= 3) return;
    const t = setInterval(() => setVisibleIdx(i => (i + 1) % winners.length), 2200);
    return () => clearInterval(t);
  }, [winners.length]);

  // Fallback: if no winners, show a subtle "Be the next winner" call-to-action
  if (winners.length === 0) {
    return (
      <div className="relative overflow-hidden rounded-2xl bg-royal-radial p-4 mb-4 shadow-xl border-2 border-yellow-400/40" data-testid="winners-ticker-empty">
        <div className="absolute inset-0 pattern-grid opacity-40" />
        <div className="relative flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gold-gradient text-blue-900 flex items-center justify-center shadow-xl shrink-0">
            <Trophy className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-black">🏆 Today&apos;s Winners</div>
            <div className="font-display font-black text-lg text-white leading-tight">Be the next winner!</div>
            <div className="text-xs text-blue-100">Place your bid and win big today</div>
          </div>
        </div>
      </div>
    );
  }

  // Show top 3 rolling
  const displayList = [];
  for (let i = 0; i < Math.min(3, winners.length); i++) {
    displayList.push(winners[(visibleIdx + i) % winners.length]);
  }

  const initialsFor = (name) => {
    const clean = (name || "").replace(/\*+/g, "").trim();
    return clean.split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase() || "P";
  };
  const avatarColors = [
    "from-rose-500 to-pink-600",
    "from-emerald-500 to-teal-600",
    "from-indigo-500 to-purple-600",
    "from-amber-500 to-orange-600",
    "from-sky-500 to-cyan-600",
    "from-fuchsia-500 to-rose-600",
  ];

  return (
    <div className="relative overflow-hidden rounded-2xl bg-royal-radial mb-4 shadow-2xl border-2 border-yellow-400/60" data-testid="winners-ticker">
      {/* Header strip */}
      <div className="relative bg-gradient-to-r from-blue-950 via-blue-800 to-blue-950 py-2 px-4 border-b-2 border-yellow-400/60 flex items-center justify-between">
        <div className="absolute inset-0 pattern-grid opacity-40" />
        <div className="relative flex items-center gap-1.5">
          <Trophy className="w-4 h-4 text-yellow-400 animate-pulse" fill="currentColor" />
          <span className="text-[11px] font-black uppercase tracking-[0.2em] text-gold-shine">Today&apos;s Winners</span>
        </div>
        <div className="relative flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse shadow-[0_0_6px_rgba(74,222,128,0.8)]" />
          <span className="text-[10px] font-bold text-green-300">LIVE</span>
        </div>
      </div>

      {/* Winners list */}
      <div className="p-2.5 space-y-2 relative">
        <div className="absolute inset-0 pattern-grid opacity-20" />
        {displayList.map((w, idx) => (
          <div
            key={`${w.id || idx}-${visibleIdx}`}
            data-testid="winner-row"
            className="relative flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-3 py-2 border border-yellow-400/30 animate-slide-in-right"
            style={{ animationDelay: `${idx * 60}ms` }}
          >
            <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColors[(w.id ? w.id.charCodeAt(0) : idx) % avatarColors.length]} text-white font-black flex items-center justify-center shadow-lg shrink-0 border-2 border-yellow-400/60`}>
              <span className="text-sm">{initialsFor(w.name)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm truncate leading-tight">🎉 {w.name}</div>
              <div className="text-[10px] text-blue-100 flex items-center gap-1">
                <span>just won</span>
                {w.market_name && <span className="text-yellow-300 font-bold">· {w.market_name}</span>}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-display font-black text-yellow-400 text-lg leading-none tabular-nums drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]">
                ₹{Number(w.amount || 0).toLocaleString("en-IN")}
              </div>
              <div className="text-[9px] text-yellow-200 font-bold uppercase tracking-widest mt-0.5">Won</div>
            </div>
          </div>
        ))}
      </div>

      {/* Total winners today footer */}
      <div className="relative bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 py-1.5 px-4 text-center border-t border-yellow-400/40">
        <span className="text-[10px] font-bold text-yellow-300 tracking-wider">
          {winners.length}+ WINNERS TODAY · <span className="text-white">Aap agle winner ho sakte ho!</span>
        </span>
      </div>
    </div>
  );
}

/* Branded SVG icons — match mockup real-world look */
function DepositIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8" aria-hidden="true" fill="none">
      <path d="M4 10a3 3 0 0 1 3-3h15a3 3 0 0 1 3 3v3h-4a3 3 0 0 0 0 6h4v3a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V10z" fill="#fff" opacity="0.98"/>
      <circle cx="21" cy="16" r="1.5" fill="#059669" />
      <g transform="translate(23,7)">
        <circle r="7" fill="#22d3ee" stroke="#fff" strokeWidth="1.8" />
        <path d="M0 -3.5 V3.5 M-3.5 0 H3.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
      </g>
    </svg>
  );
}
function WithdrawalIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8" aria-hidden="true" fill="none">
      <circle cx="16" cy="16" r="13" fill="#fff" opacity="0.98"/>
      <path d="M11 21 L21 11" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" />
      <path d="M13 11 H21 V19" stroke="#dc2626" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}
function TelegramIcon() {
  /* Official Telegram paper plane */
  return (
    <svg viewBox="0 0 240 240" className="w-8 h-8" aria-hidden="true">
      <defs>
        <linearGradient id="tg-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
      </defs>
      <path fill="url(#tg-g)" d="M81.229 128.772l14.237 39.406s1.78 3.687 3.686 3.687c1.906 0 30.255-29.492 30.255-29.492l31.525-60.89-79.448 37.225z" opacity="0.95"/>
      <path fill="#fff" d="M100.106 138.878l-2.733 29.046s-1.144 8.9 7.754 0c8.899-8.9 17.417-15.764 17.417-15.764"/>
      <path fill="#fff" d="M81.486 130.179L52.2 120.636s-3.5-1.42-2.373-4.64c.232-.664.7-1.229 2.1-2.2 6.489-4.523 120.106-45.36 120.106-45.36s3.208-1.081 5.1-.362a2.766 2.766 0 0 1 1.885 2.055c.133.726.187 1.46.165 2.193-.008.633-.084 1.219-.142 2.138-.572 9.21-17.554 77.503-17.554 77.503s-1.016 4-4.66 4.135a6.654 6.654 0 0 1-4.825-1.878c-7.116-6.122-31.71-22.65-37.149-26.285a1.55 1.55 0 0 1-.668-1.103c-.075-.575.55-1.288.55-1.288s42.815-38.05 43.957-42.044c.088-.31-.244-.46-.69-.326-2.836.844-52.085 32.05-57.508 35.486a2.7 2.7 0 0 1-1.62.064z"/>
    </svg>
  );
}
function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-7 h-7" aria-hidden="true">
      <path fill="#fff" d="M16.003 3.2c-7.066 0-12.8 5.733-12.8 12.8 0 2.255.59 4.45 1.71 6.387L3.2 28.8l6.585-1.713a12.78 12.78 0 0 0 6.218 1.585c7.066 0 12.8-5.734 12.8-12.8 0-3.42-1.332-6.638-3.752-9.057A12.722 12.722 0 0 0 16.003 3.2zm0 23.36a10.5 10.5 0 0 1-5.354-1.466l-.384-.227-3.905 1.017 1.04-3.806-.25-.394A10.55 10.55 0 0 1 5.44 16c0-5.83 4.733-10.56 10.563-10.56 2.823 0 5.475 1.099 7.47 3.094a10.5 10.5 0 0 1 3.093 7.466c0 5.83-4.733 10.56-10.563 10.56zm5.79-7.91c-.318-.16-1.878-.927-2.169-1.033-.29-.106-.502-.16-.715.16-.212.318-.82 1.033-1.005 1.246-.185.212-.37.238-.688.08-.318-.16-1.34-.494-2.553-1.575-.943-.84-1.58-1.878-1.766-2.196-.185-.318-.02-.49.139-.648.143-.142.318-.37.477-.555.16-.185.212-.318.318-.53.107-.212.053-.398-.026-.557-.08-.16-.715-1.726-.98-2.366-.259-.625-.522-.54-.715-.55l-.609-.012a1.171 1.171 0 0 0-.847.398c-.29.318-1.112 1.086-1.112 2.65 0 1.564 1.138 3.075 1.297 3.288.16.212 2.241 3.42 5.43 4.798.76.328 1.353.523 1.815.67.762.243 1.456.208 2.005.126.611-.092 1.878-.768 2.143-1.51.265-.74.265-1.378.185-1.51-.08-.132-.291-.212-.61-.371z"/>
    </svg>
  );
}
function HeadphonesIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden="true" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" fill="#fff" />
    </svg>
  );
}

/* Countdown helpers removed per user request — close-time countdown disabled. */

/* --- Premium market backgrounds & cursive taglines --- */
const MARKET_THEMES = {
  desawar:      { img: "https://images.unsplash.com/photo-1548013146-72479768bada?w=600&q=70&auto=format&fit=crop", tag: "King of Satta" },
  delhi:        { img: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600&q=70&auto=format&fit=crop", tag: "Play Your Luck" },
  ganesh:       { img: "https://images.unsplash.com/photo-1567878806014-4b0f5c8d8b4a?w=600&q=70&auto=format&fit=crop", tag: "Good Numbers Brings Good Luck" },
  mumbai:       { img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=70&auto=format&fit=crop", tag: "City of Dreams" },
  faridabad:    { img: "https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600&q=70&auto=format&fit=crop", tag: "Fortune Favors You" },
  gaziyabad:    { img: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&q=70&auto=format&fit=crop", tag: "Rise and Win" },
  ghaziabad:    { img: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&q=70&auto=format&fit=crop", tag: "Rise and Win" },
  kalyan:       { img: "https://images.unsplash.com/photo-1602081957921-9137a5d6eaee?w=600&q=70&auto=format&fit=crop", tag: "Royal Winner" },
  main:         { img: "https://images.unsplash.com/photo-1602081957921-9137a5d6eaee?w=600&q=70&auto=format&fit=crop", tag: "Big League Play" },
  time:         { img: "https://images.unsplash.com/photo-1501139083538-0139583c060f?w=600&q=70&auto=format&fit=crop", tag: "Right Time to Win" },
};
const DEFAULT_THEMES = [
  { img: "https://images.unsplash.com/photo-1602081957921-9137a5d6eaee?w=600&q=70&auto=format&fit=crop", tag: "Feel the Fortune" },
  { img: "https://images.unsplash.com/photo-1568932225a-14b0bb0e14e5?w=600&q=70&auto=format&fit=crop", tag: "Bet Bold Win Big" },
  { img: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=600&q=70&auto=format&fit=crop", tag: "Dream Big Play Bigger" },
  { img: "https://images.unsplash.com/photo-1533106418989-88406c7cc8ca?w=600&q=70&auto=format&fit=crop", tag: "Chance is Yours" },
];
function marketTheme(name = "") {
  const lower = name.toLowerCase();
  for (const key in MARKET_THEMES) {
    if (lower.includes(key)) return MARKET_THEMES[key];
  }
  let h = 0; for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return DEFAULT_THEMES[Math.abs(h) % DEFAULT_THEMES.length];
}

function MarketCard({ m, onChart }) {
  const navigate = useNavigate();
  const holiday = m.is_holiday;
  const playable = m.is_market_active && !holiday;
  const today = m.today_result || "**";
  const yesterday = m.yesterday_result || "**";
  const openTime = format12(m.open_time);
  const closeTime = format12(m.close_time);
  const theme = marketTheme(m.name);
  const bgImage = m.image_url || theme.img;
  const tagline = theme.tag;

  const handleCardClick = () => {
    if (playable) navigate(`/market/${m.id}`);
  };

  return (
    <div
      role={playable ? "button" : undefined}
      tabIndex={playable ? 0 : undefined}
      onClick={playable ? handleCardClick : undefined}
      onKeyDown={playable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleCardClick(); } } : undefined}
      className={`relative rounded-3xl overflow-hidden shadow-2xl market-nft-card border border-yellow-400/40 ${playable ? "cursor-pointer active:scale-[0.99] transition-transform" : ""}`}
      data-testid={`market-card-${m.id}`}
    >
      {/* Background image (right side) with dark blue gradient overlay */}
      <div className="absolute inset-0">
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-blue-950/85 to-blue-900/30" />
        <div className="absolute inset-0 pattern-grid opacity-20" />
      </div>

      {/* Glow orbs */}
      <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl ${holiday ? "bg-orange-400/25" : playable ? "bg-yellow-400/25" : "bg-rose-500/20"}`} />

      {/* Top-left status pill */}
      <div className={`absolute top-3 left-3 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full backdrop-blur-sm border shadow-lg ${holiday ? "bg-orange-500/90 border-orange-300" : playable ? "bg-rose-500 border-rose-300" : "bg-slate-700/90 border-slate-400"}`}>
        <span className={`w-1.5 h-1.5 rounded-full bg-white ${playable ? "animate-pulse shadow-[0_0_8px_rgba(255,255,255,0.9)]" : ""}`} />
        <span className="text-[9px] font-black uppercase tracking-[0.18em] text-white">
          {holiday ? "HOLIDAY" : playable ? "LIVE NOW" : "CLOSED"}
        </span>
      </div>

      {/* Top-right chart badge */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChart(); }}
        data-testid={`market-chart-${m.id}`}
        className="absolute top-3 right-3 z-20 flex items-center gap-1 px-3 py-1 rounded-full bg-blue-950/70 backdrop-blur-sm border border-yellow-400/60 text-yellow-300 text-[10px] font-black hover:bg-blue-900 active:scale-95 transition tracking-[0.15em] shadow-lg"
        aria-label="View chart"
      >
        <BarChart3 className="w-3 h-3" strokeWidth={2.5} />
        CHART
      </button>

      {/* Cursive tagline (bottom-right) */}
      <div className="absolute right-32 bottom-16 z-10 text-right pointer-events-none" data-testid="market-tagline">
        <div className="font-cursive italic text-yellow-300/90 text-lg leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]" style={{ fontFamily: "'Dancing Script', 'Great Vibes', cursive" }}>
          {tagline.split(" ").map((w, i, arr) => (
            <span key={i} className="block">{w}{i < arr.length - 1 ? "" : ""}</span>
          )).slice(0, 3)}
        </div>
      </div>

      {/* Main content grid */}
      <div className="relative pt-12 pb-4 px-4 grid grid-cols-[1fr_auto] gap-3 items-center">
        <div className="min-w-0">
          <div className="font-display font-black text-3xl text-gold-shine leading-none tracking-tight truncate" data-testid="market-name">
            {m.name.toUpperCase()}
          </div>
          <div className="mt-1 text-[10px] font-black text-blue-200/80 tracking-[0.25em]">RESULT · {today !== "**" ? "TODAY" : "PENDING"}</div>

          {/* PREV → TODAY jodi boxes */}
          <div className="mt-3 flex items-center gap-2">
            <div className="bg-blue-900/70 backdrop-blur-sm border border-yellow-400/30 rounded-xl px-3 py-1.5 min-w-[64px] text-center">
              <div className="text-[8px] font-black text-white/50 tracking-[0.2em]">PREV</div>
              <div className="font-display font-black text-2xl text-white tabular-nums leading-tight" data-testid="market-old">{yesterday}</div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-yellow-400" strokeWidth={2.5} />
            <div className={`bg-blue-900/70 backdrop-blur-sm border rounded-xl px-3 py-1.5 min-w-[64px] text-center ${today !== "**" ? "border-yellow-400" : "border-yellow-400/30"}`}>
              <div className="text-[8px] font-black text-yellow-300 tracking-[0.2em]">TODAY</div>
              <div className={`font-display font-black text-2xl tabular-nums leading-tight ${today !== "**" ? "text-yellow-400 drop-shadow-[0_0_10px_rgba(251,191,36,0.7)]" : "text-white/50"}`} data-testid="market-new">{today}</div>
            </div>
          </div>

          {/* Time chips */}
          <div className="mt-3 flex items-center gap-2">
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.6)]" />
              <span className="font-black text-green-300 tracking-widest">OPEN</span>
              <span className="font-money text-white tabular-nums font-bold">{openTime}</span>
            </div>
            <span className="w-px h-3 bg-white/20" />
            <div className="flex items-center gap-1 text-[10px]">
              <span className="w-2 h-2 rounded-full bg-rose-400 shadow-[0_0_4px_rgba(251,113,133,0.6)]" />
              <span className="font-black text-rose-300 tracking-widest">CLOSE</span>
              <span className="font-money text-white tabular-nums font-bold">{closeTime}</span>
            </div>
          </div>
        </div>

        {/* RIGHT: PLAY / HOLIDAY / CLOSED + live players underneath */}
        <div className="relative shrink-0 flex flex-col items-center gap-2">
          {holiday ? (
            <div
              data-testid={`holiday-btn-${m.id}`}
              className="relative flex flex-col items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 via-amber-500 to-orange-700 text-white shadow-2xl border-4 border-orange-200/80"
            >
              <span className="absolute inset-2 rounded-full border-2 border-dashed border-orange-100/40 nft-spin-slow" />
              <span className="relative text-2xl">🎉</span>
              <span className="relative text-[10px] font-black tracking-[0.15em] mt-0.5">HOLIDAY</span>
            </div>
          ) : playable ? (
            <Link
              to={`/market/${m.id}`}
              data-testid={`play-btn-${m.id}`}
              className="relative flex flex-col items-center justify-center w-24 h-24 rounded-full bg-gold-gradient text-blue-950 shadow-[0_10px_28px_rgba(251,191,36,0.7)] active:scale-95 transition-transform nft-play-chip border-4 border-yellow-200/80"
              aria-label="Play"
            >
              <span className="absolute inset-2 rounded-full border-2 border-dashed border-yellow-900/30 nft-spin-slow" />
              <Play className="relative w-8 h-8 fill-blue-950 ml-1" strokeWidth={0} />
              <span className="relative text-[10px] font-black tracking-[0.2em] mt-0.5">PLAY</span>
            </Link>
          ) : (
            <div
              data-testid={`timeout-btn-${m.id}`}
              className="relative flex flex-col items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-rose-500 to-red-700 text-white shadow-2xl border-4 border-rose-200/70"
            >
              <span className="absolute inset-2 rounded-full border-2 border-dashed border-rose-100/30" />
              <span className="relative text-3xl">⏰</span>
              <span className="relative text-[10px] font-black tracking-[0.15em] mt-0.5">CLOSED</span>
            </div>
          )}
          {/* Live players — directly under Play button, centered */}
          <LivePlayers seed={`mkt-${m.id}`} />
        </div>
      </div>
    </div>
  );
}
